/**
 * A short hint on hover or focus, from Radix; shadcn's wrapper.
 *
 * A shadcn primitive: kept close to as generated so it can be updated from
 * shadcn. TooltipProvider wraps the app once (App.tsx).
 *
 * Used by: App.tsx, for TooltipProvider around the signed-in layout. The
 * app's own tooltips go through shared/context/TooltipContext and
 * shared/hooks/useTooltip; nothing imports Tooltip or TooltipContent from
 * here today.
 *
 * @module components/ui/tooltip
 */
import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { cn } from "@/lib/utils"

/** Shares the hover delay across every tooltip; mounted once around the app. */
const TooltipProvider = TooltipPrimitive.Provider

/** One tooltip: its trigger and its content. */
const Tooltip = TooltipPrimitive.Root

/** What is hovered or focused to show the hint. */
const TooltipTrigger = TooltipPrimitive.Trigger

/** The hint itself, a small box beside its trigger. */
const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      "z-50 overflow-hidden rounded-md border border-border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
      className
    )}
    {...props}
  />
))
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } 